import { type AxiosError } from 'axios'
import { type APIError } from '@mc-alberta/types'
import { type AuthenticateError } from '@/types/AuthenticateError'
import { ERRORS } from '@/constants'

type SubError = Omit<AuthenticateError, 'name'>

/**
 * Formats an SDK-compliant error.
 */
export function getAuthenticateError(
  error: string | AxiosError<APIError> | Error,
  reason?: string
): AuthenticateError {
  if (typeof error === 'string') {
    return {
      name: 'authError',
      reason: reason || ERRORS.AUTH_ERROR,
      message: error
    }
  }

  if ((error as AxiosError).isAxiosError) {
    return {
      name: 'authError',
      ...getServiceError(error as AxiosError<APIError>)
    }
  }

  return {
    name: 'authError',
    reason: ERRORS.UNKNOWN_ERROR
  }
}

/**
 * Returns the error details specific to the given 400-level request error.
 */
function get400Error(error: APIError): SubError {
  if (error.reason === ERRORS.INVALID_STATE) {
    const threeDsError = error.details.find((a) => a.source === 'MIServer')

    if (threeDsError) {
      return {
        reason: ERRORS.AUTH_ERROR,
        message: threeDsError.message
      }
    }

    return {
      reason: ERRORS.INVALID_REQUEST,
      details: error.details
    }
  }

  return {
    reason: ERRORS.INVALID_PARAMETER,
    details: error.details
  }
}

/**
 * Formats an API-level error into an SDK-compliant one.
 */
function getServiceError(error: AxiosError<APIError>): SubError {
  switch (error.response?.status) {
    case 400:
      return get400Error(error.response.data)
    case 401:
      return { reason: ERRORS.PERMISSION_DENIED }
    case 403:
      return { reason: ERRORS.ACCT_INACCESSIBLE }
    case 404:
      return { reason: ERRORS.NOT_FOUND }
    default:
      return { reason: ERRORS.SERVICE_ERROR }
  }
}
